import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, AlertCircle, Lock, Database } from 'lucide-react'
import { getContentModels, getContentList } from '../../lib/api/content'
import { pickColumns, formatCell, modelTitle } from '../../lib/contentFormat'
import { NAV, isGroup, isLabel } from '../../lib/nav'

function modelForPath(pathname: string): string | undefined {
  for (const e of NAV) {
    if (isGroup(e)) {
      const hit = e.items.find((i) => i.to === pathname)
      if (hit?.model) return hit.model
    } else if (!isLabel(e) && e.to === pathname && e.model) return e.model
  }
  return undefined
}

const PAGE_SIZE = 50

export function ContentListPage() {
  const { pathname } = useLocation()
  const model = modelForPath(pathname)
  const [page, setPage] = useState(0)

  const models = useQuery({ queryKey: ['contentModels'], queryFn: getContentModels, staleTime: Infinity })
  const modelDef = models.data?.find((m) => m.id === model)

  const list = useQuery({
    queryKey: ['content', model, page],
    queryFn: () => getContentList(model!, { offset: page * PAGE_SIZE, limit: PAGE_SIZE }),
    enabled: Boolean(model),
    placeholderData: keepPreviousData,
  })

  const columns = useMemo(() => pickColumns(modelDef), [modelDef])
  const rows = list.data?.data ?? []
  const total = list.data?.totalCount ?? 0
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1
  const to = Math.min((page + 1) * PAGE_SIZE, total)
  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1)

  if (!model) {
    return <div className="p-5 text-sm text-ink-500">Sezione non configurata.</div>
  }

  return (
    <div className="w-full p-5">
      {/* header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold tracking-tight text-ink-900">
            {modelTitle(modelDef, model)}
          </h2>
          <span className="badge bg-ink-100 text-ink-600">
            <Database className="h-3 w-3" /> {total.toLocaleString('it-IT')}
          </span>
          {modelDef?.readOnly && (
            <span className="badge bg-ink-100 text-ink-500">
              <Lock className="h-3 w-3" /> sola lettura
            </span>
          )}
        </div>
      </div>

      {/* table */}
      <div className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-ink-200 bg-ink-50">
                {columns.map((c) => (
                  <th key={c.field} className="whitespace-nowrap px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wide text-ink-500">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.isError ? (
                <tr>
                  <td colSpan={columns.length || 1} className="px-3 py-12 text-center text-sm text-ink-500">
                    <AlertCircle className="mx-auto mb-2 h-5 w-5 text-brand-600" /> Impossibile caricare i dati.
                  </td>
                </tr>
              ) : list.isLoading || !modelDef ? (
                Array.from({ length: 12 }).map((_, i) => (
                  <tr key={i} className="border-b border-ink-100">
                    {(columns.length ? columns : Array.from({ length: 6 })).map((_c, j) => (
                      <td key={j} className="px-3 py-2.5">
                        <div className="skeleton h-3.5 w-24 rounded-sm bg-ink-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length || 1} className="px-3 py-12 text-center text-sm text-ink-400">
                    Nessun record nel periodo.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="border-b border-ink-100 last:border-0 hover:bg-ink-50/70">
                    {columns.map((c) => (
                      <td key={c.field} className="whitespace-nowrap px-3 py-2 text-ink-700">
                        {formatCell(row.elements[c.field], c)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* pagination */}
        <div className="flex items-center justify-between border-t border-ink-200 px-3 py-2 text-xs text-ink-500">
          <span className="tnum">
            {from.toLocaleString('it-IT')}–{to.toLocaleString('it-IT')} di {total.toLocaleString('it-IT')}
          </span>
          <div className="flex items-center gap-1">
            <button
              className="btn btn-default px-2 py-1 disabled:opacity-40"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || list.isFetching}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-2 tnum">
              {page + 1} / {maxPage + 1}
            </span>
            <button
              className="btn btn-default px-2 py-1 disabled:opacity-40"
              onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
              disabled={page >= maxPage || list.isFetching}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
